import {date, makeRange, MINUTES_PER_HOUR} from '@eitje/utils'
import {config} from '../../../config'
import * as API from '../../../requests'
import {useWhere as getWhere, useFind as getFind, useAll as getAll, useIndex as doIndex} from '../../../hooks'
import utils from '@eitje/utils'
import {useShow as doShow} from './hooks/use_show'
import {useWhereNot as doWhereNot} from './hooks/use_where_not'
import {useFetchShow as doFetchShow} from './hooks/use_fetch_show'
import {useActiveRecordPermissions} from '../../permissions'
import {getActiveRecordScopeGroup} from '../../scopes'
import autoBind from '../../lib/autobind'
import _ from 'lodash'

// IMPORTANT NOTE: we used to use JS mixins to organize functionality, but that has a serious perf penalty,
// 								 so we decided to put all methods in the same file. Not very pretty, but fast

class ActiveRecord {
	//
	// BASE
	//

	get modelConfig() {
		return config.models?.[this.tableName] || {}
	}

	static get modelConfig() {
		return config.models?.[this.tableName] || {}
	}

	constructor(props) {
		Object.assign(this, props)
		if (this.modelConfig?.enrich) {
			const extraProps = this.modelConfig.enrich(this)
			Object.assign(this, extraProps)
		}
		autoBind(this)
	}

	tableName = this.constructor.tableName

	snakeTableName() {
		return utils.camelToSnake(this.tableName)
	}

	static snakeTableName() {
		return utils.camelToSnake(this.tableName)
	}

	createdAt() {
		if (this._createdAt) return this._createdAt
		this._createdAt = date(this.created_at)
		return this._createdAt
	}

	updatedAt() {
		if (this._updatedAt) return this._updatedAt
		this._updatedAt = date(this.updated_at)
		return this._updatedAt
	}

	idWithTableName() {
		return `${this.tableName}-${this.id}`
	}

	//
	// QUERY METHODS
	//

	static useAll(opts) {
		return getAll(this.tableName, opts)
	}

	static useWhere(query, opts) {
		return getWhere(this.tableName, query, opts)
	}

	static useWhereNot(query, opts) {
		return doWhereNot(this.tableName, query, opts)
	}

	static useFind(query, opts) {
		return getFind(this.tableName, query, opts)
	}

	//
	// PERMISSIONS
	//

	// we use a class field with a named function to adhere to React Hooks rules
	static usePermissions = function usePermissions() {
		return useActiveRecordPermissions(this)
	}

	// Use getters so scope groups bind to the subclass being accessed, such as
	// TimeRegistrationShift, instead of the base ActiveRecord constructor.
	static get allowedAssociatedRecords() {
		return getActiveRecordScopeGroup(this, 'allowedAssociatedRecords')
	}

	// Affordances expose permission-shaped options and constraints for model
	// attributes, such as disabled choices or date bounds.
	static get affordances() {
		return getActiveRecordScopeGroup(this, 'affordances')
	}

	//
	// ACTIONS
	//

	static create(params, opts) {
		return API.create(this.tableName, params, opts)
	}

	static useDoIndex(params, extra) {
		return doIndex(utils.camelToSnake(this.tableName), params, extra)
	}

	static index(params) {
		return API.index(this.snakeTableName(), params)
	}

	static update = this.create

	static destroy(id) {
		return API.destroy(this.snakeTableName(), id)
	}

	static useDoFetchShow(id, params) {
		return doFetchShow(this.snakeTableName(), id, params)
	}

	static useShow(params = {}) {
		return doShow(utils.camelToSnake(this.tableName), params)
	}

	static arbitrary(endpoint, params = {}, rest = {}) {
		return API.arbitrary(utils.camelToSnake(this.tableName), endpoint, {params, ...rest})
	}

	static multiUpdate(params, rest = {}) {
		return API.updateMulti(this.snakeTableName(), params, rest)
	}

	static getByIds(ids, extraParams = {}) {
		return API.getByIds(this.snakeTableName(), ids, extraParams)
	}

	refresh() {
		return API.show(this.snakeTableName(), this.id)
	}

	update(params, rest = {}) {
		return API.update(this.tableName, {...params, id: this.id}, rest)
	}

	destroy() {
		return API.destroy(this.snakeTableName(), this.id)
	}

	buildUrl(path) {
		return `${utils.camelToSnake(this.tableName)}/${this.id}/${path}`
	}

	addRemoveAssoc(data, existingData) {
		data = _.omit(data, 'id') // we don't wanna pass ID to addRemoveAssoc, it has to be 'pure', only assocs
		return API.addRemoveAssoc(this.snakeTableName(), data, existingData, this.id)
	}

	resourceReq(endpoint, params = {}) {
		params = {...params, id: this.id}
		if (params.method) {
			params = {params: _.omit(params, 'method'), method: params.method}
		}

		return API.resourceReq(utils.camelToSnake(this.tableName), endpoint, params)
	}

	updateLocal(params) {
		return API.createLocal(this.tableName, {...params, id: this.id})
	}
}

class DateRangeActiveRecord extends ActiveRecord {
	static startDateField = 'start_date'
	static endDateField = 'end_date'

	constructor(props) {
		super(_.omit(props, ['dateRange', '_dateRange']))
		// We define dateRange as an enumerable own property to ensure it works with object spreading (...record).
		// Standard class getters (on the prototype) are not copied during spread operations.
		// This approach maintains the lazy-loading behavior (via the getter) while treating dateRange as a first-class citizen.
		Object.defineProperty(this, 'dateRange', {
			enumerable: true,
			get: () => {
				if (this._dateRange) return this._dateRange
				this._dateRange = makeRange(this._startDate(), this._endDate())
				return this._dateRange
			},
		})
	}

	_startDate = () => this[this.constructor.startDateField]
	_endDate = () => this[this.constructor.endDateField]

	totalMins() {
		return this.dateRange.diff('minutes')
	}

	totalDays() {
		return this.dateRange.diff('days')
	}

	stringMins() {
		return utils.minToTimeString(this.totalMins())
	}

	totalHours() {
		return this.totalMins() / MINUTES_PER_HOUR
	}

	static totalMins() {
		return utils.sum(this.map(i => i.totalMins()))
	}
}

export {DateRangeActiveRecord}

export default ActiveRecord
